/**
 * THE CONNECTION TO WHERE A SEASON ACTUALLY LIVES.
 *
 * Everything a contractor draws lives in his browser today. A cleared browser
 * takes the season with it, Safari deletes script-writable storage after seven
 * quiet days, and a job quoted on a phone is not on the laptop. This is the
 * other end of the fix. See supabase/schema.sql for the tables.
 *
 * THE KEY IN HERE IS PUBLIC ON PURPOSE.
 *
 * A publishable key is designed to ship inside a browser bundle; it is the
 * front door, not the lock. What actually stops one company reading another's
 * customer list is row level security in the database, where every policy is
 * account_id = auth.uid(). If those policies were ever dropped, this key would
 * be a hole - which is the real reason schema.sql turns them on before
 * anything else.
 *
 * It is BAKED IN rather than read from an environment variable, for two
 * reasons. A Netlify build fails when an env var's value appears anywhere in
 * the repository, and this project has already lost three deploys to that
 * scanner. And a key fetched at boot from the marketing site would mean nobody
 * can sign in whenever that site is slow or down. An override is read from the
 * environment at build time for whoever needs one; the default needs no
 * configuration at all.
 *
 * THE SECRET KEY IS NOT HERE AND NEVER WILL BE. It stays in the dashboard. A
 * secret key in a browser bundle is every row in the database, readable by
 * anyone who opens the developer tools.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const URL_DEFAULT = 'https://tktbpantpxonskmddnbd.supabase.co'
const KEY_DEFAULT = 'sb_publishable_3o0_7pRS2I_U-EcVK-za7g_2EJ1-ka7'

/*
 * import.meta.env is Vite's, and it does not exist under the node test runner.
 * Reaching for it unguarded threw on import, which took down every test that
 * so much as imported a file that imported this one. The defaults are the
 * answer everywhere anyway; the override is a build-time convenience.
 */
const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {}
const url = env.VITE_SUPABASE_URL || URL_DEFAULT
const key = env.VITE_SUPABASE_KEY || KEY_DEFAULT

/**
 * One client for the whole app.
 *
 * Two would each keep their own copy of the session and drift the moment one
 * of them refreshed a token, which shows up as a signed in person being told
 * to sign in again on a screen they did not touch.
 */
export const supa: SupabaseClient = createClient(url, key, {
  auth: {
    // The session is this origin's localStorage. app.lightsos.com and
    // lightsos.com are different origins and do not share it, which is why
    // a sign in on the site has to hand the session over. See handoff.ts.
    persistSession: true,
    autoRefreshToken: true,
    // The app reads the hash itself, once, and strips it. Doing it here as
    // well means two things racing to consume the same tokens.
    detectSessionInUrl: false,
  },
})

/** True when the browser has a session it can act on right now. */
export async function signedIn(): Promise<boolean> {
  const { data } = await supa.auth.getSession()
  return !!data.session
}
